import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Input,
  Spinner,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { FiTrash2, FiPlus } from "react-icons/fi";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { AppBar, AppIcon, APP } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import { appImages } from "../../assets/appicons";
import {
  getLockingRelativeParticipantListApi,
  bulkCreateLockingRelativeParticipantApi,
} from "../../api/finance.locking.api";
import { getMyRelativeListApi } from "../../api/finance.collaborator.api";
import { extractErrorMessage } from "../../api/client";

const TEXT = "#08142D";
const MUTED = "#94A3B8";
const BORDER = "#E1E1E1";
const LIMIT = 50;

/* ------------------------------------------------------------ helpers (khớp app) */
const clampRate = (value) => {
  const numeric = Number(String(value ?? "").replace(/[^\d]/g, ""));
  if (Number.isNaN(numeric)) return 0;
  return Math.max(0, Math.min(100, numeric));
};

const groupRelativeParticipants = (list = []) => {
  const groups = [];
  const members = list.filter((item) => !item.isCollaborator);
  members.forEach((member) => {
    const relatives = list.filter(
      (item) => item.isCollaborator && item.relativeId === member.employeeId
    );
    groups.push({ member, relatives });
  });
  return groups;
};

const flattenRelativeGroups = (groups = []) => {
  let result = [];
  groups.forEach(({ member, relatives }) => {
    result.push(member);
    result = result.concat(relatives || []);
  });
  return result;
};

// Kiểm tra hợp lệ (mirror zod superRefine của app). Trả về message lỗi đầu tiên hoặc "".
const validateStaffList = (list) => {
  const groups = groupRelativeParticipants(list);
  if (!groups.length) return "Không có nhân viên tham gia để chia tỷ lệ";

  const memberIds = groups.map((g) => g.member.employeeId).filter(Boolean);
  if (new Set(memberIds).size !== memberIds.length) return "Chuyên viên kinh doanh bị trùng";

  for (const group of groups) {
    const member = group.member;
    const relative = group.relatives[0];
    if (!member.employeeId) return "Thiếu nhân viên chính";
    if (group.relatives.length > 1)
      return `Nhân viên ${member.employee?.fullName || ""} chỉ được có tối đa 1 Cộng tác viên`;

    const memberRate = Number(member.relativeCommissionRate ?? 100);
    if (!relative) {
      if (memberRate !== 100)
        return `Nếu chưa chọn Cộng tác viên thì tỷ lệ của ${member.employee?.fullName || "nhân viên"} phải bằng 100%`;
      continue;
    }
    if (!relative.employeeId)
      return `Vui lòng chọn Cộng tác viên cho ${member.employee?.fullName || "nhân viên"}`;
    if (relative.employeeId === member.employeeId)
      return `Cộng tác viên không được trùng với nhân viên chính (${member.employee?.fullName || ""})`;
    const relativeRate = Number(relative.relativeCommissionRate ?? 0);
    if (memberRate + relativeRate !== 100)
      return `Tổng tỷ lệ của ${member.employee?.fullName || "nhân viên"} và Cộng tác viên phải bằng 100%`;
  }
  return "";
};

/* ---------------------------------------------------------------- CTV picker (bottom sheet) */
const RelativePickerModal = ({ isOpen, onClose, employeeId, selectedId, onSelect }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const reqId = useRef(0);

  const load = useCallback(
    async (kw) => {
      if (!employeeId) {
        setList([]);
        return;
      }
      reqId.current += 1;
      const rid = reqId.current;
      setLoading(true);
      try {
        const data = await getMyRelativeListApi({
          page: 1,
          limit: LIMIT,
          employeeId,
          search: kw || undefined,
        });
        if (rid !== reqId.current) return;
        setList(data?.items || data || []);
      } catch {
        if (rid === reqId.current) setList([]);
      } finally {
        if (rid === reqId.current) setLoading(false);
      }
    },
    [employeeId]
  );

  useEffect(() => {
    if (isOpen) load("");
  }, [isOpen, load]);

  const onSearchChange = (e) => {
    const v = e.target.value;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => load(v.trim()), 250);
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="bottom">
      <DrawerOverlay bg="rgba(0,0,0,0.45)" />
      <DrawerContent borderTopRadius="16px" maxW="480px" mx="auto" maxH="80vh">
        <DrawerBody py="16px" px="16px">
          <Flex align="center" justify="space-between" mb="8px">
            <Text fontSize="16px" fontWeight={700} color={TEXT}>Chọn thành viên</Text>
            <Box as="button" onClick={onClose}>
              <AppIcon src={appImages.icon_close} size={20} color={MUTED} />
            </Box>
          </Flex>
          <Flex h="44px" align="center" px="12px" bg="white" border="1px solid" borderColor={APP.gray} borderRadius="8px">
            <AppIcon src={appImages.icon_search} size={20} color={APP.gray} />
            <Input variant="unstyled" ml="8px" fontSize="13px" placeholder="Tìm kiếm ..." onChange={onSearchChange} />
          </Flex>
          <Box mt="8px" maxH="55vh" overflowY="auto">
            {loading ? (
              <Flex justify="center" py="30px"><Spinner color={APP.primary200} /></Flex>
            ) : list.length === 0 ? (
              <Text fontSize="14px" color={MUTED} textAlign="center" py="30px">Không có dữ liệu</Text>
            ) : (
              list.map((item) => {
                const active = item._id === selectedId;
                return (
                  <Flex
                    key={item._id}
                    as="button"
                    w="100%"
                    align="center"
                    gap="4px"
                    py="14px"
                    px="4px"
                    borderBottom="1px solid #F1F4F9"
                    textAlign="left"
                    onClick={() => { onSelect(item); onClose(); }}
                  >
                    <Text fontSize="14px" fontWeight={600} color={active ? APP.primary200 : TEXT}>{item.fullName || ""}</Text>
                    <Text fontSize="14px" fontWeight={600} color={active ? APP.primary200 : TEXT}>{item.code ? `(${item.code})` : ""}</Text>
                  </Flex>
                );
              })
            )}
          </Box>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

/* ---------------------------------------------------------------- input helpers */
const Label = ({ title, required }) => (
  <Flex align="center" gap="4px" pb="6px">
    <Text fontSize="13px" fontWeight={500} color={TEXT}>{title}</Text>
    {required && <Text fontSize="13px" color="#E5484D">*</Text>}
  </Flex>
);

const fieldWrap = {
  h: "52px",
  bg: "white",
  border: "1px solid",
  borderColor: BORDER,
  borderRadius: "8px",
  px: "16px",
  align: "center",
};

/* ---------------------------------------------------------------- group card */
const RelativeGroupCard = ({ index, group, isDetail, onUpdateGroup }) => {
  const picker = useDisclosure();
  const member = group.member;
  const relative = group.relatives[0];
  const memberRate = Number(member.relativeCommissionRate ?? 100);
  const relativeRate = Number(relative?.relativeCommissionRate ?? 0);

  const handleMemberRateChange = (value) => {
    const nextMemberRate = clampRate(value);
    const nextRelativeRate = Math.max(0, 100 - nextMemberRate);
    onUpdateGroup(
      index,
      { relativeCommissionRate: nextMemberRate },
      relative ? [{ ...relative, relativeCommissionRate: nextRelativeRate }] : []
    );
  };

  const handleRelativeRateChange = (value) => {
    if (!relative) return;
    const nextRelativeRate = clampRate(value);
    const nextMemberRate = Math.max(0, 100 - nextRelativeRate);
    onUpdateGroup(index, { relativeCommissionRate: nextMemberRate }, [
      { ...relative, relativeCommissionRate: nextRelativeRate },
    ]);
  };

  const handleSelectRelative = (item) => {
    const nextRelative = {
      ...(relative || {}),
      lockingId: member.lockingId,
      employeeId: item._id,
      employee: item,
      relativeId: member.employeeId,
      isCollaborator: true,
      commissionRate: member.commissionRate,
      relativeCommissionRate: relative
        ? Number(relative.relativeCommissionRate ?? 0)
        : Math.max(0, 100 - memberRate),
    };
    onUpdateGroup(index, undefined, [nextRelative]);
  };

  const handleAddRelative = () => {
    if (relative) return;
    const nextRelative = {
      lockingId: member.lockingId,
      employeeId: "",
      relativeId: member.employeeId,
      isCollaborator: true,
      commissionRate: member.commissionRate,
      relativeCommissionRate: Math.max(0, 100 - memberRate),
      employee: { _id: "", fullName: "" },
    };
    onUpdateGroup(index, undefined, [nextRelative]);
  };

  const handleDeleteRelative = () => onUpdateGroup(index, undefined, []);

  return (
    <Box mx="16px" mt="16px" bg="white" border="1px solid" borderColor={BORDER} borderRadius="14px" p="16px">
      <Flex justify="space-between" align="center" mb="14px">
        <Flex align="center" gap="8px">
          <Flex minW="24px" h="24px" px="8px" borderRadius="12px" bg={APP.primary200} align="center" justify="center">
            <Text fontSize="12px" fontWeight={700} color="white">{index + 1}</Text>
          </Flex>
          <Text fontSize="15px" fontWeight={600} color={TEXT}>Nhân viên tham gia</Text>
        </Flex>
        {!isDetail && relative ? (
          <Flex as="button" align="center" gap="6px" onClick={handleDeleteRelative}>
            <FiTrash2 color="#E5484D" size={16} />
            <Text fontSize="13px" fontWeight={600} color="#E5484D">Xóa</Text>
          </Flex>
        ) : <Box />}
      </Flex>

      <Label title="Chuyên viên kinh doanh" required />
      <Flex {...fieldWrap} opacity={1}>
        <Text fontSize="14px" color={TEXT} noOfLines={1}>{member.employee?.fullName || ""}</Text>
      </Flex>

      <Box mt="12px">
        <Label title="Tỷ lệ chuyên viên kinh doanh" required />
        <Flex {...fieldWrap} opacity={isDetail ? 0.6 : 1}>
          <Input
            variant="unstyled"
            fontSize="14px"
            type="text"
            inputMode="numeric"
            value={String(memberRate)}
            isReadOnly={isDetail}
            onChange={(e) => handleMemberRateChange(e.target.value)}
          />
          <Text fontSize="14px" color={TEXT}>%</Text>
        </Flex>
      </Box>

      <Box mt="12px">
        <Label title="Cộng tác viên" />
        {relative ? (
          <Box>
            <Flex
              {...fieldWrap}
              justify="space-between"
              as={isDetail ? "div" : "button"}
              opacity={isDetail ? 0.6 : 1}
              onClick={isDetail ? undefined : picker.onOpen}
            >
              <Text fontSize="14px" color={relative.employee?.fullName ? TEXT : MUTED} noOfLines={1}>
                {relative.employee?.fullName || "Chọn thành viên"}
              </Text>
              {!isDetail && <AppIcon src={appImages.icon_chevron_down} size={20} color={MUTED} />}
            </Flex>
            <Box mt="12px">
              <Label title="Tỷ lệ cộng tác viên" />
              <Flex {...fieldWrap} opacity={isDetail ? 0.6 : 1}>
                <Input
                  variant="unstyled"
                  fontSize="14px"
                  type="text"
                  inputMode="numeric"
                  value={String(relativeRate)}
                  isReadOnly={isDetail}
                  onChange={(e) => handleRelativeRateChange(e.target.value)}
                />
                <Text fontSize="14px" color={TEXT}>%</Text>
              </Flex>
            </Box>
            {!isDetail && (
              <RelativePickerModal
                isOpen={picker.isOpen}
                onClose={picker.onClose}
                employeeId={member.employeeId}
                selectedId={relative.employee?._id}
                onSelect={handleSelectRelative}
              />
            )}
          </Box>
        ) : isDetail ? (
          <Flex {...fieldWrap} opacity={0.6}>
            <Text fontSize="14px" color={MUTED}>Chưa có Cộng tác viên</Text>
          </Flex>
        ) : (
          <Flex
            as="button"
            mt="0"
            h="44px"
            borderRadius="10px"
            border="1px solid"
            borderColor={APP.primary200}
            align="center"
            justify="center"
            gap="6px"
            w="100%"
            onClick={handleAddRelative}
          >
            <FiPlus color={APP.primary200} size={16} />
            <Text fontSize="13px" fontWeight={600} color={APP.primary200}>Thêm Cộng tác viên</Text>
          </Flex>
        )}
      </Box>
    </Box>
  );
};

/* ---------------------------------------------------------------- screen */
const LockingRelativeForm = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { id } = useParams();
  const [params] = useSearchParams();
  const isDetail = params.get("type") === "detail";

  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getLockingRelativeParticipantListApi({ page: 1, limit: LIMIT, lockingId: id })
      .then((res) => {
        const mapped = (res?.items || res || []).map((item) => ({
          lockingId: item.lockingId || id,
          employeeId: item.employeeId || item.employee?._id || "",
          commissionRate: Number(item.commissionRate ?? 0),
          isCollaborator: !!item.isCollaborator,
          relativeCommissionRate: Number(
            item.relativeCommissionRate ?? (item.isCollaborator ? 0 : 100)
          ),
          employee: item.employee,
          relativeId: item.relativeId,
        }));
        setStaffList(mapped);
      })
      .catch(() => setStaffList([]))
      .finally(() => setLoading(false));
  }, [id]);

  const groupData = useMemo(() => groupRelativeParticipants(staffList), [staffList]);

  const handleUpdateGroup = useCallback(
    (groupIndex, memberUpdate, relativeUpdate) => {
      setStaffList((prev) => {
        const currentGroups = groupRelativeParticipants(prev);
        const nextGroups = currentGroups.map((group, idx) => {
          if (idx !== groupIndex) return group;
          return {
            member: memberUpdate ? { ...group.member, ...memberUpdate } : group.member,
            relatives: relativeUpdate !== undefined ? relativeUpdate : group.relatives,
          };
        });
        return flattenRelativeGroups(nextGroups);
      });
    },
    []
  );

  const onSubmit = async () => {
    if (!id) { toast.error("Thiếu lockingId"); return; }
    const err = validateStaffList(staffList);
    if (err) { toast.error(err); return; }

    const body = {
      lockingId: String(id),
      list: staffList
        .filter((item) => item.employeeId)
        .map((item) => ({
          lockingId: String(id),
          employeeId: item.employeeId,
          relativeId: item.relativeId,
          isCollaborator: item.isCollaborator,
          commissionRate: Number(item.commissionRate ?? 0),
          relativeCommissionRate: Number(item.relativeCommissionRate ?? 0),
        })),
    };

    setSubmitting(true);
    try {
      await bulkCreateLockingRelativeParticipantApi(body);
      toast.success("Cập nhật người thân/CTV thành công");
      navigate(-1);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Cập nhật người thân/CTV thất bại"));
    } finally {
      setSubmitting(false);
    }
  };

  const pageBody = (
    <>

      {loading ? (
        <Flex justify="center" py="60px"><Spinner color={APP.primary200} /></Flex>
      ) : (
        <>
          <Box flex="1" overflowY="auto" pb="16px">
            {groupData.length ? (
              groupData.map((group, index) => (
                <RelativeGroupCard
                  key={`${group.member.employeeId || index}`}
                  index={index}
                  group={group}
                  isDetail={isDetail}
                  onUpdateGroup={handleUpdateGroup}
                />
              ))
            ) : (
              <Text fontSize="14px" color={MUTED} textAlign="center" pt="40px">
                Chưa có nhân viên tham gia
              </Text>
            )}
          </Box>

          {!isDetail && (
            <Box position="sticky" bottom="0" bg="white" borderTop="1px solid #EEF1F6" px="16px" py="12px">
              <Flex gap="16px">
                <Button
                  flex="1"
                  h="50px"
                  variant="outline"
                  borderColor={APP.primary200}
                  color={APP.primary200}
                  borderRadius="8px"
                  _hover={{ bg: "#EAF2FF" }}
                  onClick={() => navigate(-1)}
                >
                  Hủy
                </Button>
                <Button
                  flex="1"
                  h="50px"
                  bg={APP.primary200}
                  color="white"
                  borderRadius="8px"
                  _hover={{ bg: APP.primary100 }}
                  isLoading={submitting}
                  onClick={onSubmit}
                >
                  Lưu
                </Button>
              </Flex>
            </Box>
          )}
        </>
      )}
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title={isDetail ? "Chi tiết CTV tham gia" : "Thêm CTV tham gia hợp đồng"} right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" bg="#F6F8FC" minH="100dvh" display="flex" flexDirection="column">
      <AppBar title={isDetail ? "Chi tiết CTV tham gia" : "Thêm CTV tham gia hợp đồng"} />
      {pageBody}
    </Box>
  );
};

export default LockingRelativeForm;
